import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Mail, Lock, Phone, Calendar, PawPrint, Clock, Hourglass, Heart, Bell, Settings, Video } from 'lucide-react';
import { toast } from 'sonner';
import MesAnimaux from '../components/MesAnimaux';
import NotificationCenter from '../components/NotificationCenter';
import NotificationPreferences from '../components/NotificationPreferences';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import { PatientTeleconsultationJoin } from '../components/Teleconsultation';
import { PatientProfileEditor } from '../components/profile/ProfileEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EspacePatientPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [patientStatus, setPatientStatus] = useState(null); // pending, approved, rejected
  const [patientProfile, setPatientProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('login');
  const [dashboardView, setDashboardView] = useState('overview'); // overview, animals, settings
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Real appointments from API
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [animals, setAnimals] = useState([]);
  
  // Teleconsultation state
  const [pendingTeleconsultations, setPendingTeleconsultations] = useState([]);
  const [showTeleconsultation, setShowTeleconsultation] = useState(false);
  const [selectedTeleconsultation, setSelectedTeleconsultation] = useState(null);

  // Fetch pending teleconsultations
  const fetchPendingTeleconsultations = async () => {
    const token = localStorage.getItem('patientToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/teleconsultation/my-pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingTeleconsultations(data);
      }
    } catch (error) {
      console.error('Error fetching teleconsultations:', error);
    }
  };

  // Fetch animals
  const fetchAnimals = async () => {
    const token = localStorage.getItem('patientToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/animals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnimals(data);
      }
    } catch (error) {
      console.error('Error fetching animals:', error);
    }
  };

  // Fetch appointments when logged in
  const fetchAppointments = async () => {
    const token = localStorage.getItem('patientToken');
    if (!token) return;
    
    setLoadingAppointments(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
    setLoadingAppointments(false);
  };

  // Fetch on login
  useEffect(() => {
    if (isLoggedIn && patientStatus === 'approved') {
      fetchAppointments();
      fetchAnimals();
      fetchPendingTeleconsultations();
      
      // Check for teleconsultations every 30 seconds
      const interval = setInterval(fetchPendingTeleconsultations, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, patientStatus]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Check if user is a patient
        if (data.user.role !== 'patient') {
          toast.error('Ce compte n\'est pas un compte patient');
          setIsLoading(false);
          return;
        }
        
        localStorage.setItem('patientToken', data.access_token);
        setPatientProfile(data.user);
        setPatientStatus(data.user.status || 'pending');
        setIsLoggedIn(true);
        
        if (data.user.status === 'pending') {
          toast.success('Connexion réussie ! Votre compte est en attente de validation.');
        } else if (data.user.status === 'approved') {
          toast.success('Connexion réussie !');
        } else {
          toast.error('Votre compte a été refusé.');
        }
      } else {
        toast.error(data.detail || 'Email ou mot de passe incorrect');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    }
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          email: registerForm.email,
          password: registerForm.password,
          name: `${registerForm.firstName} ${registerForm.lastName}`,
          role: 'patient'
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('patientToken', data.access_token);
        setPatientProfile(data.user);
        setPatientStatus('pending');
        setIsLoggedIn(true);
        toast.success('Inscription réussie ! Votre compte est en attente de validation par l\'administrateur.');
      } else {
        toast.error(data.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('patientToken');
    setIsLoggedIn(false);
    setPatientProfile(null);
    setPatientStatus(null);
    setLoginForm({ email: '', password: '' });
    toast.success('Déconnexion réussie');
  };

  // Render pending approval message for patients
  const renderPendingApproval = () => (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-grow flex items-center justify-center py-12">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Hourglass className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              Inscription en cours de validation
            </h1>
            <p className="text-gray-600 mb-6">
              Votre demande d'inscription a bien été reçue. Notre équipe examine votre dossier et vous recevrez une confirmation par email sous 24 à 48 heures.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-green-700">
                <strong>Email :</strong> {patientProfile?.email}
              </p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full">
              Se déconnecter
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );

  // Show pending page if patient is not approved yet
  if (isLoggedIn && patientStatus === 'pending') {
    return renderPendingApproval();
  }

  // Show rejected message
  if (isLoggedIn && patientStatus === 'rejected') {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center py-12">
          <div className="max-w-md mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Inscription refusée
              </h1>
              <p className="text-gray-600 mb-6">
                Malheureusement, votre demande d'inscription n'a pas été acceptée. 
                Veuillez contacter notre support pour plus d'informations.
              </p>
              <Button onClick={handleLogout} variant="outline" className="w-full">
                Se déconnecter
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (isLoggedIn) {
    // Show MesAnimaux component if in animals view
    if (dashboardView === 'animals') {
      return (
        <div className="min-h-screen flex flex-col bg-gray-50">
          <Header />
          <main className="flex-grow py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <MesAnimaux 
                token={localStorage.getItem('patientToken')} 
                onBack={() => setDashboardView('overview')}
              />
            </div>
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 text-white mb-8">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Bonjour, {patientProfile?.name?.split(' ')[0] || 'Patient'} !</h1>
                  <p className="text-green-100">Gérez vos rendez-vous et le carnet de santé de vos animaux</p>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={handleLogout} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                      Se déconnecter
                    </Button>
                    <Button 
                      onClick={() => setDashboardView('settings')} 
                      variant="outline" 
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                      data-testid="settings-btn"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Paramètres
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <NotificationCenter token={localStorage.getItem('patientToken')} />
                </div>
              </div>
            </div>

            {/* Settings View */}
            {dashboardView === 'settings' && (
              <div className="mb-8">
                <button
                  onClick={() => setDashboardView('overview')}
                  className="flex items-center gap-2 text-gray-600 hover:text-green-600 mb-6"
                >
                  <span>← Retour</span>
                </button>
                
                {/* Profile Section */}
                <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                  <PatientProfileEditor 
                    token={localStorage.getItem('patientToken')} 
                    onUpdate={(updated) => setPatientProfile({...patientProfile, ...updated})}
                  />
                </div>
                
                {/* Notification Preferences */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-6">
                    <Bell className="w-5 h-5 text-green-600" />
                    Préférences de notifications
                  </h2>
                  <NotificationPreferences token={localStorage.getItem('patientToken')} />
                </div>
              </div>
            )}

            {dashboardView === 'overview' && (
            <div className="space-y-6">
              {/* Teleconsultation Alert */}
              {pendingTeleconsultations.length > 0 && (
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg animate-pulse-slow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                        <Video className="w-7 h-7" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">Téléconsultation en attente</h3>
                        <p className="text-blue-100">
                          {pendingTeleconsultations[0].vet_name} vous attend pour {pendingTeleconsultations[0].animal_name}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        setSelectedTeleconsultation(pendingTeleconsultations[0]);
                        setShowTeleconsultation(true);
                      }}
                      className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 py-3"
                      data-testid="join-teleconsultation-alert-btn"
                    >
                      <Video className="w-5 h-5 mr-2" />
                      Rejoindre maintenant
                    </Button>
                  </div>
                </div>
              )}

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Appointments */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-green-600" />
                      Mes rendez-vous
                    </h2>
                    <Link to="/recherche">
                      <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white">
                        + Prendre RDV
                      </Button>
                    </Link>
                  </div>

                  <div className="space-y-4">
                    {loadingAppointments ? (
                      <div className="text-center py-8">
                        <Clock className="w-8 h-8 animate-spin text-green-500 mx-auto mb-3" />
                        <p className="text-gray-500">Chargement...</p>
                      </div>
                    ) : appointments.length > 0 ? appointments.map((apt) => (
                      <div key={apt.appointment_id} className="border border-gray-100 rounded-xl p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-800">{apt.clinic_name}</h3>
                            <p className="text-sm text-gray-500 mt-1">{apt.animal_name} ({apt.animal_type}) - {apt.appointment_type}</p>
                            <p className="text-sm text-green-600 mt-2 font-medium">
                              {new Date(apt.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {apt.time}
                            </p>
                            {apt.clinic_city && (
                              <p className="text-xs text-gray-400 mt-1">{apt.clinic_address}, {apt.clinic_city}</p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            apt.status === 'confirmed' 
                              ? 'bg-green-100 text-green-700' 
                              : apt.status === 'completed'
                              ? 'bg-blue-100 text-blue-700'
                              : apt.status === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {apt.status === 'confirmed' ? 'Confirmé' :
                             apt.status === 'pending' ? 'En attente' :
                             apt.status === 'completed' ? 'Terminé' :
                             apt.status === 'cancelled' ? 'Annulé' : apt.status}
                          </span>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-8 text-gray-500">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Aucun rendez-vous</p>
                        <p className="text-sm mb-4">Prenez rendez-vous avec un vétérinaire pour commencer.</p>
                        <Link to="/recherche">
                          <Button className="bg-green-500 hover:bg-green-600 text-white">
                            Trouver un vétérinaire
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Animals - Quick Access Card */}
              <div>
                <div 
                  className="bg-white rounded-2xl shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-green-200"
                  onClick={() => setDashboardView('animals')}
                  data-testid="animals-quick-access"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      <Heart className="w-5 h-5 text-green-600" />
                      Carnet de santé
                    </h2>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Nouveau</span>
                  </div>

                  <div className="space-y-3">
                    {animals.length > 0 ? (
                      <>
                        {animals.slice(0, 3).map((animal) => (
                          <div key={animal.animal_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center text-xl">
                              {animal.species === 'chien' ? '🐕' : 
                               animal.species === 'chat' ? '🐱' : 
                               animal.species === 'lapin' ? '🐰' : '🐾'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-800 truncate">{animal.name}</h3>
                              <p className="text-xs text-gray-500">{animal.breed || animal.species}</p>
                            </div>
                          </div>
                        ))}
                        {animals.length > 3 && (
                          <p className="text-sm text-gray-500 text-center">+{animals.length - 3} autre(s)</p>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <PawPrint className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-500">Aucun animal</p>
                      </div>
                    )}
                  </div>

                  <Button 
                    className="w-full mt-4 bg-green-500 hover:bg-green-600 text-white"
                    data-testid="open-health-book-btn"
                  >
                    <PawPrint className="w-4 h-4 mr-2" />
                    {animals.length > 0 ? 'Voir le carnet de santé' : 'Ajouter un animal'}
                  </Button>
                </div>
              </div>
            </div>
            </div>
            )}
          </div>
        </main>
        
        {/* Teleconsultation Modal */}
        {showTeleconsultation && selectedTeleconsultation && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Video className="w-6 h-6 text-blue-600" />
                  Téléconsultation avec {selectedTeleconsultation.vet_name}
                </h2>
                <button
                  onClick={() => {
                    setShowTeleconsultation(false);
                    setSelectedTeleconsultation(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <div className="p-6">
                <PatientTeleconsultationJoin
                  roomName={selectedTeleconsultation.room_name}
                  onClose={() => {
                    setShowTeleconsultation(false);
                    setSelectedTeleconsultation(null);
                    fetchPendingTeleconsultations();
                  }}
                />
              </div>
            </div>
          </div>
        )}
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-grow py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">Espace Patient</h1>
              <p className="text-gray-500 mt-2">Gérez vos rendez-vous vétérinaires</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 mb-6">
                <TabsTrigger value="login">Connexion</TabsTrigger>
                <TabsTrigger value="register">Inscription</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="votre@email.com"
                        className="pl-10"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="password">Mot de passe</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white">
                    Se connecter
                  </Button>
                  <p className="text-center text-sm text-gray-500">
                    <Link to="/forgot-password" className="text-green-600 hover:underline">Mot de passe oublié ?</Link>
                  </p>
                  
                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">ou</span>
                    </div>
                  </div>
                  
                  {/* Google Login */}
                  <GoogleLoginButton redirectPath="/mon-espace" />
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">Prénom</Label>
                      <Input
                        id="firstName"
                        placeholder="Jean"
                        value={registerForm.firstName}
                        onChange={(e) => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Nom</Label>
                      <Input
                        id="lastName"
                        placeholder="Dupont"
                        value={registerForm.lastName}
                        onChange={(e) => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="regEmail">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="regEmail"
                        type="email"
                        placeholder="votre@email.com"
                        className="pl-10"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">Téléphone</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        className="pl-10"
                        value={registerForm.phone}
                        onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="regPassword">Mot de passe</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="regPassword"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white">
                    S'inscrire
                  </Button>
                  
                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">ou</span>
                    </div>
                  </div>
                  
                  {/* Google Login */}
                  <GoogleLoginButton redirectPath="/mon-espace" />
                  <p className="text-xs text-gray-500 text-center mt-2">
                    En vous connectant avec Google, un compte sera créé automatiquement
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EspacePatientPage;
